import React, { useEffect, useMemo, useRef, useState } from "react";
import { listCoupons, createCoupon, deleteCoupon, updateCoupon } from "../../services/analytics";
import { listCustomers } from "../../services/analytics";
import { Plus, Trash2, X, Ticket, ToggleLeft, ToggleRight, Search } from "lucide-react";

const EMPTY = { code: "", discount_percent: 10, discount_amount: 0, user_id: "", max_uses: 0, expires_at: "", active: true };

// ─────────────────────────────────────────────────────────────────────
// Searchable customer combobox — replaces the native <select>.
// Lets the admin type an email / name fragment to filter the list.
// ─────────────────────────────────────────────────────────────────────
function CustomerSearchSelect({ users, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selected = users.find((u) => u.uid === value);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const pool = users.filter((u) => u.role !== "admin");
    if (!term) return pool.slice(0, 80);
    return pool.filter((u) =>
      (u.email || "").toLowerCase().includes(term) ||
      (u.full_name || "").toLowerCase().includes(term) ||
      (u.phone || "").toLowerCase().includes(term)
    ).slice(0, 80);
  }, [users, q]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 text-left flex items-center justify-between"
        data-testid="coupon-customer-trigger"
      >
        <span className={selected ? "" : "text-[var(--text-muted)]"}>
          {selected ? (selected.email || selected.full_name || selected.uid) : "Hamıya"}
        </span>
        <span className="metadata text-[var(--text-muted)]">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 z-10 bg-[var(--bg)] border border-[var(--border)] shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border-soft)]">
            <Search size={14} className="text-[var(--text-muted)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Müştəri axtar (email / ad / telefon)…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="coupon-customer-search"
            />
            {q && <button type="button" onClick={() => setQ("")} className="text-[var(--text-muted)]"><X size={14} /></button>}
          </div>
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface)] ${!value ? "bg-[var(--surface)] font-medium" : ""}`}
            >
              <span className="metadata text-[var(--text-muted)]">→ </span>Hamıya (limit yoxdur)
            </button>
            {filtered.map((u) => (
              <button
                key={u.uid}
                type="button"
                onClick={() => { onChange(u.uid); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface)] ${value === u.uid ? "bg-[var(--surface)] text-[var(--primary)]" : ""}`}
                data-testid={`coupon-customer-opt-${u.uid}`}
              >
                <div className="truncate">{u.email || u.full_name || u.uid}</div>
                {(u.full_name || u.phone) && (
                  <div className="metadata text-[var(--text-muted)] truncate">
                    {u.full_name}{u.full_name && u.phone ? " · " : ""}{u.phone}
                  </div>
                )}
              </button>
            ))}
            {!filtered.length && (
              <div className="px-3 py-6 text-center text-sm text-[var(--text-muted)] metadata">NƏTİCƏ TAPILMADI</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = async () => setItems(await listCoupons());
  useEffect(() => { reload(); listCustomers().then(setUsers); }, []);

  const save = async () => {
    if (!editing.code) return alert("Kod tələb olunur.");
    await createCoupon(editing);
    setEditing(null); reload();
  };

  const toggleActive = async (c) => {
    await updateCoupon(c.id, { active: !c.active });
    reload();
  };

  const del = async (c) => {
    if (!window.confirm(`'${c.code}' silinsin?`)) return;
    await deleteCoupon(c.id); reload();
  };

  const userEmail = (uid) => users.find((u) => u.uid === uid)?.email || uid;

  return (
    <div className="p-10" data-testid="admin-coupons">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="metadata">ENDİRİMLƏR</div>
          <h1 className="font-display text-5xl mt-1">Kuponlar</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="add-coupon"><Plus size={14} /> KUPON ƏLAVƏ ET</button>
      </div>

      <div className="border border-[var(--border)]">
        <div className="grid grid-cols-12 metadata px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">
          <div className="col-span-2">KOD</div>
          <div className="col-span-2">ENDİRİM</div>
          <div className="col-span-3">MÜŞTƏRİ</div>
          <div className="col-span-2">İSTİFADƏ</div>
          <div className="col-span-1">AKTİV</div>
          <div className="col-span-2 text-right">ƏMƏL</div>
        </div>
        {items.map((c) => (
          <div key={c.id} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--border-soft)] items-center text-sm" data-testid={`coupon-${c.id}`}>
            <div className="col-span-2 font-mono text-[var(--primary)]">{c.code}</div>
            <div className="col-span-2 font-display">
              {c.discount_percent > 0 ? `${c.discount_percent}%` : ""}
              {c.discount_amount > 0 ? ` ₼${c.discount_amount}` : ""}
            </div>
            <div className="col-span-3 metadata text-[var(--text-2)] truncate">{c.user_id ? userEmail(c.user_id) : "Hamıya"}</div>
            <div className="col-span-2 font-mono text-xs">{c.used || 0} / {c.max_uses || "∞"}</div>
            <div className="col-span-1">
              <button onClick={() => toggleActive(c)}>
                {c.active ? <ToggleRight size={20} className="text-[var(--primary)]" /> : <ToggleLeft size={20} className="text-[var(--text-muted)]" />}
              </button>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => del(c)} className="p-1 hover:text-[var(--accent)]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!items.length && <div className="p-10 text-center text-[var(--text-muted)] metadata">HƏLƏ KUPON YOXDUR</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl flex items-center gap-2"><Ticket size={20} /> Yeni Kupon</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="metadata">KOD <span className="text-[var(--text-muted)] normal-case">— müştərinin yazacağı endirim kodu</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono uppercase" placeholder="WELCOME10" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="coupon-code" />
              </div>
              <div>
                <label className="metadata">FAİZ (%) <span className="text-[var(--text-muted)] normal-case">— səbətin faizlə endirimi</span></label>
                <input type="number" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.discount_percent} onChange={(e) => setEditing({ ...editing, discount_percent: e.target.value })} />
              </div>
              <div>
                <label className="metadata">SABİT AZN <span className="text-[var(--text-muted)] normal-case">— konkret məbləğ endirim</span></label>
                <input type="number" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.discount_amount} onChange={(e) => setEditing({ ...editing, discount_amount: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="metadata">MÜŞTƏRİ <span className="text-[var(--text-muted)] normal-case">— boş buraxsanız hamıya açıqdır</span></label>
                <CustomerSearchSelect
                  users={users}
                  value={editing.user_id}
                  onChange={(uid) => setEditing({ ...editing, user_id: uid })}
                />
              </div>
              <div>
                <label className="metadata">MAX İSTİFADƏ <span className="text-[var(--text-muted)] normal-case">— 0 = limitsiz</span></label>
                <input type="number" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} />
              </div>
              <div>
                <label className="metadata">BİTMƏ TARİXİ <span className="text-[var(--text-muted)] normal-case">— istəyə bağlı</span></label>
                <input type="date" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-outline">LƏĞV ET</button>
              <button onClick={save} className="btn-primary" data-testid="coupon-save">YADDA SAXLA</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
